function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/* eslint-disable camelcase,no-dupe-class-members,no-use-before-define */
import cf from 'clownface';
import ResourceFactoryImpl from "./lib/ResourceFactory.mjs";
import once from 'once';
import TypeCollectionCtor from "./lib/TypeCollection.mjs";
import { xsd } from '@tpluscode/rdf-ns-builders';
import { defaultGraphInstance } from '@rdf-esm/data-model';
import { toJSON } from "./lib/toJSON.mjs";
import { fromInitializer } from "./lib/resource.mjs";
import { mixins } from "./lib/mixins.mjs";
import { toLiteral } from "./lib/conversion.mjs";
export default class RdfResourceImpl {
  static _userInitializeProperties(resource, init = {}) {
    Object.entries(init).filter(([prop]) => prop !== 'id' && prop !== 'types').forEach(([prop, value]) => {
      if (!prop.startsWith('http')) {
        // use decorated setter property
        if (typeof value === 'function') {
          resource[prop] = value(resource.pointer.any());
        } else {
          resource[prop] = value;
        }

        return;
      }

      const values = Array.isArray(value) ? value : [value];
      const pointers = values.map(function toPointer(value) {
        if (typeof value === 'function') {
          const result = value(resource.pointer.any());

          if (typeof result === 'function') {
            throw new Error('Initializer factory function cannot return a function');
          }

          if (Array.isArray(result)) {
            throw new Error('Initializer factory function cannot return an array');
          }

          return toPointer(result);
        }

        if (typeof value === 'object' && 'term' in value) {
          return value.term;
        }

        if (typeof value === 'object' && 'pointer' in value) {
          return value.pointer;
        }

        if (typeof value === 'object' && 'termType' in value) {
          return resource.pointer.node(value);
        }

        let literal;

        if (typeof value === 'object' && 'value' in value && 'datatype' in value) {
          literal = toLiteral(value.value, value.datatype);
        } else {
          literal = toLiteral(value);
        }

        if (literal) {
          return resource.pointer.node(literal);
        } // create and initialize an object resource


        const childResource = fromInitializer(resource, value);
        return childResource.pointer;
      });
      resource.pointer.addOut(resource.pointer.namedNode(prop), pointers);
    });

    for (const type of init.types || []) {
      resource.types.add(type);
    }
  }

  constructor(pointer, init = {}, parent) {
    _defineProperty(this, "pointer", void 0);

    _defineProperty(this, "unionGraphPointer", void 0);

    _defineProperty(this, "__initialized", false);

    _defineProperty(this, "_parent", void 0);

    _defineProperty(this, "__initializeProperties", void 0);

    if (pointer.term.termType !== 'BlankNode' && pointer.term.termType !== 'NamedNode') {
      throw new Error(`RdfResource cannot be initialized from a ${pointer.term.termType} node`);
    }
    /* TODO: when clownface gets graph feature
    if (pointer._context[0].graph) {
      this.pointer = pointer
      this.unionGraphPointer = pointer.fromGraph(null)
    } else {
      this.pointer = pointer.fromGraph(pointer.defaultGraph)
      this.unionGraphPointer = pointer
    } */


    const selfGraph = cf(_objectSpread(_objectSpread({}, pointer), {}, {
      term: pointer.term
    }));

    if (selfGraph._context[0].graph) {
      this.pointer = selfGraph;
      this.unionGraphPointer = cf({
        dataset: selfGraph.dataset,
        term: selfGraph.term,
        graph: undefined
      });
    } else {
      this.pointer = cf({
        dataset: selfGraph.dataset,
        term: selfGraph.term,
        graph: defaultGraphInstance
      });
      this.unionGraphPointer = cf({
        dataset: selfGraph.dataset,
        term: selfGraph.term
      });
    }

    this.__initializeProperties = once(() => {
      const self = this;
      const defaults = [...mixins(self)].flatMap(mixinProto => [...mixinProto.__initializers]);
      defaults.forEach(([key, value]) => {
        const currentValue = self[key];
        const valueIsEmptyArray = Array.isArray(currentValue) && currentValue.length === 0;
        const valueIsUndefined = typeof currentValue === 'undefined';

        if (!(valueIsEmptyArray || valueIsUndefined)) {
          return;
        }

        if (typeof value === 'function') {
          self[key] = value(self);
          return;
        }

        self[key] = value;
      });
      return true;
    });
    this._parent = parent;
    this.__initialized = this.__initializeProperties();

    RdfResourceImpl._userInitializeProperties(this, init);
  }

  get id() {
    return this.pointer.term;
  }

  get _graphId() {
    return this.pointer._context[0].graph;
  }

  get types() {
    return new TypeCollectionCtor(this);
  }

  get isAnonymous() {
    return this.id.termType === 'BlankNode';
  }

  hasType(type) {
    return this.types.has(type);
  }

  equals(other) {
    if (!other) {
      return false;
    }

    if ('termType' in other) {
      return this.id.equals(other);
    }

    const otherPointer = '_context' in other ? other : other.pointer;
    const idsEqual = this.id.equals(otherPointer.term);

    if (this.isAnonymous || otherPointer.term.termType === 'BlankNode') {
      return idsEqual && this.pointer.dataset === otherPointer.dataset;
    }

    return idsEqual;
  }

  get(property, options) {
    const objects = this.getArray(property, options);

    if (objects.length > 0) {
      return objects[0];
    }

    return null;
  }

  getArray(property, options) {
    const values = this._getObjects(property, options).filter(obj => obj.term.termType === 'NamedNode' || obj.term.termType === 'BlankNode').map(obj => {
      return this._create(obj, [], {
        parent: this
      });
    });

    if (!values.length) {
      return [];
    }

    return values;
  }

  getNumber(property, options) {
    const [value] = this._getObjects(property, options).toArray();

    if (typeof value === 'undefined') {
      return null;
    }

    if (value.term.termType === 'Literal') {
      return parseFloat(value.value);
    }

    throw new Error(`Expected property '${property}' to be a number but found '${value}'`);
  }

  getString(property, options) {
    const [value] = this._getObjects(property, options).toArray();

    if (typeof value === 'undefined') {
      return null;
    }

    if (value.term.termType === 'Literal') {
      return value.value;
    }

    throw new Error(`Expected property '${property}' to be a literal but found '${value}'`);
  }

  getBoolean(property, options) {
    const [value] = this._getObjects(property, options).toArray();

    if (typeof value === 'undefined') {
      return false;
    }

    if (value.term.termType === 'Literal' && xsd.boolean.equals(value.term.datatype)) {
      return value.term.equals(this.pointer.literal(true).term);
    }

    throw new Error(`Expected property '${property}' to be a boolean but found '${value}'`);
  }

  _getObjects(property, {
    strict
  } = {
    strict: false
  }) {
    const propertyNode = typeof property === 'string' ? this.pointer.namedNode(property) : property;
    const objects = this.pointer.out(propertyNode);

    if (!objects.terms.length && strict) {
      throw new Error(`Value for predicate <${property}> was missing`);
    }

    return objects;
  }

  _create(term, mixins, options = {}) {
    return this.constructor.factory.createEntity(term, mixins, options);
  }

  toJSON() {
    return toJSON(this);
  }

}

_defineProperty(RdfResourceImpl, "__ns", void 0);

_defineProperty(RdfResourceImpl, "factory", new ResourceFactoryImpl(RdfResourceImpl));

_defineProperty(RdfResourceImpl, "__mixins", []);

_defineProperty(RdfResourceImpl, "__properties", new Map());

_defineProperty(RdfResourceImpl, "__initializers", new Map());

export function fromObject(initializer) {
  return initializer;
}